import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Form } from "react-final-form";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { ThemeContext } from "styled-components";
import { CheckoutContext } from "v3/context/checkout";
import { TenantContext } from "v3/context/tenant";
import { LocalizationContext } from "v3/context/localization";
import { PaymentContext } from "v3/context/payment";
import { CheckoutErrorContext } from "v3/context/checkoutError";
import { Button } from "@pepdirect/v3/ui/Button";
import { PlusIconSvg } from "@pepdirect/v3/ui/icons";
import { OnlyAllocationAllowedAlert } from "v3/components/checkout/alerts/OnlyAllocationAllowedAlert";
import { SectionHeading } from "v3/components/checkout/SectionHeading";
import { Stripe as StripeForm } from "v3/components/forms/Stripe";
import { SavedPaymentMethods } from "v3/components/checkout/SavedPaymentMethods";
import { LegalCopy } from "v3/components/checkout/LegalCopy";
import { PoNumberInput } from "v3/components/checkout/PoNumberInput";
import {
  AllowedPaymentMethod,
  Credit,
  useAvailablePaymentMethodsLazyQuery,
  useSavePaymentMutation,
  useSavePaymentOptionsMutation,
} from "v3/services/graphql/generated";
import { getIsOrderMoreThanAllocationAmount } from "v3/helpers/checkout";
import { filterAndSortPaymentMethods } from "v3/helpers/payment";
import { PaymentAndBillingFormValues } from "v3/helpers/validation";
import { SavedPaymentMethodsType } from "v3/types/payment";
import { StyledFieldSet } from "@pepdirect/v3/ui/fields/base";
import {
  StyledAddButton,
  StyledHeading,
  StyledNewPaymentForm,
  StyledSavedPaymentMethods,
  StyledSpinner,
} from "./index.styled";
import { UnavailableAllocationToCanadaAlert } from "v3/components/checkout/alerts/UnavailableAllocationToCanada";

interface PaymentStepProps {
  continueToNextStep: () => void;
}

/**
 * THIS IS FOR STRIPE ONLY!!
 */
export const StripePaymentStep: FC<PaymentStepProps> = ({
  continueToNextStep,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const { checkoutSession, isLoading, setCheckoutSession, setIsLoading } =
    useContext(CheckoutContext);
  const { tenant } = useContext(TenantContext);
  const {
    availablePaymentMethods,
    clearPaymentDataOnEdit,
    handleSelectInvoice,
    handleSelectNewCreditCard,
    handleSelectNewP24,
    handleSelectNewBlik,
    handleSelectSavedCreditCard,
    handleSelectSavedAllocatedCredit,
    paymentNonce,
    paymentType,
    saveCardForLater,
    savePaymentInput,
    setAvailablePaymentMethods,
    setSaveCardForLater,
    setStripeTokenDetails,
    isBillingEqualShipping,
    setIsBillingEqualShipping,
  } = useContext(PaymentContext);
  const { clearAllErrors, handleCheckoutErrors, setErrorMsg } =
    useContext(CheckoutErrorContext);
  const theme = useContext(ThemeContext);
  const { genericErrorMsg, localization } = useContext(LocalizationContext);

  const {
    sectionHeadingText = "Payment",
    headingText = "Select a payment method",
    addPaymentButtonText = "Add a new payment method",
    addPaymentHeadingText = "Add a New Payment Method",
    continueToReviewButtonText = "Continue To Review",
  } = localization?.checkout?.paymentStep || {};

  const {
    contactInfo,
    customerPo,
    giftInfo,
    id,
    payment,
    user,
    hasGasExchangeItems,
    price,
  } = checkoutSession || {};

  const [paymentStepReady, setPaymentStepReady] = useState(false);
  const [sortedPaymentMethods, setSortedPaymentMethods] =
    useState<SavedPaymentMethodsType>([]);
  const [showNewPaymentForm, setShowNewPaymentForm] = useState(false);
  const [addNewPaymentDisabled, setAddNewPaymentDisabled] = useState(false);

  const [savePayment] = useSavePaymentMutation();
  const [savePaymentOptions] = useSavePaymentOptionsMutation();
  const [getAvailablePaymentMethods] = useAvailablePaymentMethodsLazyQuery();

  const isInvoiceUser = !!checkoutSession?.activeAccount?.isInvoiced;

  const isOrderMoreThanAllocationAmount = useMemo(
    () =>
      getIsOrderMoreThanAllocationAmount({
        paymentMethods: sortedPaymentMethods,
        totalPrice: price?.total,
      }),
    [price?.total, sortedPaymentMethods]
  );

  const hasActiveAllocation = useMemo(
    () =>
      sortedPaymentMethods.find(
        (paymentMethod) =>
          paymentMethod.__typename === "Credit" &&
          paymentMethod.type === "allocation"
      ),
    [sortedPaymentMethods]
  );

  useEffect(() => {
    if (isBillingEqualShipping === null) {
      setIsBillingEqualShipping(!giftInfo);
    }
  }, [isBillingEqualShipping, giftInfo, setIsBillingEqualShipping]);

  const allowedPaymentMethods = useMemo(
    () => availablePaymentMethods?.allowedPaymentMethods || [],
    [availablePaymentMethods]
  );

  const isInvoiceAllowed = useMemo(
    () => allowedPaymentMethods.includes(AllowedPaymentMethod.Invoice),
    [allowedPaymentMethods]
  );

  const orderHasSubscription = useMemo(() => {
    return checkoutSession?.price.lineItems.some(
      (item) =>
        item.__typename === "LineItem" && item.subscriptionIntervalInDays
    );
  }, [checkoutSession?.price.lineItems]);

  const isGuest = useMemo(() => !user, [user]);

  const hasSavedPaymentMethods = !!sortedPaymentMethods?.length;

  const onlyAllowAllocation =
    contactInfo?.shippingAddress?.countryCode === "CA";

  useEffect(() => {
    if (orderHasSubscription && !isGuest) {
      setSaveCardForLater(true);
    }
  }, [isGuest, orderHasSubscription, setSaveCardForLater]);

  useEffect(() => {
    if (hasGasExchangeItems) {
      setSaveCardForLater(true);
    }
  }, [hasGasExchangeItems, setSaveCardForLater]);

  useEffect(() => {
    const getPaymentMethods = async () => {
      try {
        const { data, error } = await getAvailablePaymentMethods({
          variables: { id },
        });
        if (error) throw error;

        const { availablePaymentMethods } = data?.checkoutSession || {};

        if (availablePaymentMethods) {
          setAvailablePaymentMethods(availablePaymentMethods);
          const paymentMethodsSorted = filterAndSortPaymentMethods(
            availablePaymentMethods
          );
          setSortedPaymentMethods(paymentMethodsSorted);

          if (payment?.paymentMethod === AllowedPaymentMethod.Blik) {
            handleSelectNewBlik({ paymentNonce: "" });
            setShowNewPaymentForm(true);
          } else if (payment?.paymentMethod === AllowedPaymentMethod.P24) {
            handleSelectNewP24({ paymentNonce: "" });
            setShowNewPaymentForm(true);
          } else if (!paymentMethodsSorted.length) {
            handleSelectNewCreditCard({ paymentNonce: "" });
            setShowNewPaymentForm(true);
          } else if (
            payment?.paymentMethod === AllowedPaymentMethod.CreditCard
          ) {
            handleSelectNewCreditCard({ paymentNonce: "" });
            setShowNewPaymentForm(true);
          }
        }
      } catch (e) {
        handleCheckoutErrors(e);
      } finally {
        setPaymentStepReady(true);
      }
    };
    if (!paymentStepReady) {
      getPaymentMethods();
    } else {
      setPaymentStepReady(true);
    }
  }, [
    availablePaymentMethods,
    getAvailablePaymentMethods,
    handleCheckoutErrors,
    handleSelectNewCreditCard,
    payment?.paymentMethod,
    id,
    isInvoiceAllowed,
    paymentStepReady,
    setAvailablePaymentMethods,
    handleSelectNewBlik,
    handleSelectNewP24,
  ]);

  // pre-select the first saved payment method on load
  useEffect(() => {
    if (hasSavedPaymentMethods && availablePaymentMethods) {
      if (onlyAllowAllocation) {
        const allocationCreditPaymentMethod = sortedPaymentMethods.find(
          (paymentMethod): paymentMethod is Credit =>
            paymentMethod.__typename === "Credit"
        );
        if (allocationCreditPaymentMethod && !isOrderMoreThanAllocationAmount) {
          handleSelectSavedAllocatedCredit({
            id: allocationCreditPaymentMethod.id,
          });
        } else {
          // If allocation itself is not present, clear the existing selection.
          // Clearing the selection is needed for following reason -
          // When user adds a US address on the Shipping step, and goes to Payment step, selects a payment method like Credit Card or Paypal,
          // comes back to Shipping step, adds a Canada address and again goes to Payment step, then the previously selected payment
          // method remained selected even if it is disabled for Canada. Hence calling `clearPaymentDataOnEdit()` in that case to
          // manually clear the selection is needed.
          clearPaymentDataOnEdit();
        }
        return;
      }

      const firstPaymentMethod = sortedPaymentMethods[0];
      if (firstPaymentMethod.__typename === "CreditCard") {
        const { token } = firstPaymentMethod;
        handleSelectSavedCreditCard({
          billingAddress: firstPaymentMethod.billingAddress,
          token,
        });
      } else if (
        firstPaymentMethod.__typename === "Credit" &&
        !isOrderMoreThanAllocationAmount
      ) {
        handleSelectSavedAllocatedCredit({
          id: firstPaymentMethod.id,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availablePaymentMethods, clearPaymentDataOnEdit, onlyAllowAllocation]);

  // pre-select invoice if allowed
  // if invoice, it is the only payment method allowed
  useEffect(() => {
    if (isInvoiceAllowed) {
      setAddNewPaymentDisabled(true);
      handleSelectInvoice();
    }
  }, [isInvoiceAllowed, handleSelectInvoice]);

  useEffect(() => {
    if (savePaymentInput?.savedCreditCard) setShowNewPaymentForm(false);
  }, [savePaymentInput]);

  const isContinueButtonDisabled = useCallback(
    (isFormValid: boolean) => {
      if (!showNewPaymentForm) return !savePaymentInput; // If no saved option is selected, disable the continue button
      if (
        paymentType &&
        [
          AllowedPaymentMethod.Blik,
          AllowedPaymentMethod.P24,
          AllowedPaymentMethod.CreditCard,
        ].includes(paymentType)
      )
        return !isFormValid;
    },
    [showNewPaymentForm, savePaymentInput, paymentType]
  );

  const handleChangePaymentType = (value: string) => {
    if (value === "p24") {
      handleSelectNewP24({ paymentNonce: "" });
    } else if (value === "blik") {
      handleSelectNewBlik({ paymentNonce: "" });
    } else if (value === "card") {
      handleSelectNewCreditCard({ paymentNonce: "" });
    }
  };

  const handleSetSaveCardForLater = (value: boolean) => {
    if (paymentType === AllowedPaymentMethod.CreditCard) {
      setSaveCardForLater(value);
    }
  };

  const handleShowNewPaymentForm = () => {
    handleSelectNewCreditCard({ paymentNonce: "" });
    setIsBillingEqualShipping(true);
    setShowNewPaymentForm(true);
  };

  const onSubmit = async (values: PaymentAndBillingFormValues) => {
    clearAllErrors();
    setIsLoading(true);
    const { poNumber } = values;

    // set PO number if entered, but also clear it if unset
    if (poNumber || (customerPo && !poNumber)) {
      try {
        await savePaymentOptions({
          variables: {
            id,
            input: { customerPo: poNumber || "" },
          },
        });
      } catch (e) {
        handleCheckoutErrors(e);
        setIsLoading(false);
        return;
      }
    }

    // get nonce (token) for new credit cards
    if (stripe && elements && !savePaymentInput?.savedCreditCard) {
      // https://docs.stripe.com/payments/finalize-payments-on-the-server?platform=web&type=payment&client=react#create-ct
      // Trigger form validation and wallet collection
      try {
        const { error: submitError } = await elements.submit();
        if (submitError) {
          setErrorMsg(genericErrorMsg);
          setIsLoading(false);
          return;
        }
      } catch (e) {
        setErrorMsg(genericErrorMsg);
        setIsLoading(false);
        return;
      }

      let token;

      // Create the ConfirmationToken using the details collected by the Payment Element
      // and additional shipping information
      try {
        const { error: createConfirmationTokenError, confirmationToken } =
          await stripe.createConfirmationToken({
            elements,
            params: {
              return_url: `${tenant?.fallbackUrl || ""}/v3/${
                checkoutSession?.cart?.id
              }/${tenant?.id}`,
            },
          });

        if (createConfirmationTokenError) {
          setErrorMsg(genericErrorMsg);
          setIsLoading(false);
          return;
        }
        token = confirmationToken || {};
        setStripeTokenDetails(confirmationToken);
      } catch (e) {
        setErrorMsg(genericErrorMsg);
        setIsLoading(false);
        return;
      }

      try {
        const { data } = await savePayment({
          variables: {
            id,
            input: {
              stripe: {
                clientConfirmationToken: token?.id,
                paymentMethodType: token?.payment_method_preview.type || "",
                isBillingEqualShipping: !!isBillingEqualShipping,
                saveCardForLater,
              },
            },
          },
        });
        if (data?.savePayment) setCheckoutSession(data.savePayment);
      } catch (e) {
        handleCheckoutErrors(e);
        setIsLoading(false);
        return;
      }
    } else {
      if (!savePaymentInput) return;
      try {
        const { data } = await savePayment({
          variables: {
            id,
            input: savePaymentInput,
          },
        });
        if (data?.savePayment) setCheckoutSession(data.savePayment);
      } catch (e) {
        handleCheckoutErrors(e);
        setIsLoading(false);
        return;
      }
    }

    continueToNextStep();
    setIsLoading(false);
  };

  return (
    <Form
      keepDirtyOnReinitialize
      initialValues={{ poNumber: customerPo }}
      onSubmit={onSubmit}
      render={({ handleSubmit, valid }) => (
        <form onSubmit={handleSubmit}>
          <StyledFieldSet disabled={isLoading}>
            <SectionHeading>{sectionHeadingText}</SectionHeading>
            <StyledHeading size="xs" level="h3">
              {headingText}
            </StyledHeading>
            {onlyAllowAllocation && (
              <>
                {hasActiveAllocation ? (
                  <OnlyAllocationAllowedAlert />
                ) : (
                  <UnavailableAllocationToCanadaAlert />
                )}
              </>
            )}
            {paymentStepReady && availablePaymentMethods ? (
              <>
                {hasSavedPaymentMethods && (
                  <>
                    <StyledSavedPaymentMethods>
                      <SavedPaymentMethods
                        onlyAllowAllocation={onlyAllowAllocation}
                        closeNewPaymentForm={() => setShowNewPaymentForm(false)}
                        paymentMethods={sortedPaymentMethods}
                        selectedToken={
                          paymentNonce ||
                          savePaymentInput?.savedCreditCard?.token
                        }
                        totalPrice={price?.total}
                      />
                    </StyledSavedPaymentMethods>
                    {!showNewPaymentForm &&
                      !addNewPaymentDisabled &&
                      !onlyAllowAllocation && (
                        <StyledAddButton>
                          <Button
                            onClick={handleShowNewPaymentForm}
                            type="text"
                          >
                            <PlusIconSvg color={theme.button.text.color} />
                            <span>{addPaymentButtonText}</span>
                          </Button>
                        </StyledAddButton>
                      )}
                  </>
                )}
                {showNewPaymentForm && !onlyAllowAllocation && (
                  <StyledNewPaymentForm>
                    {hasSavedPaymentMethods && (
                      <StyledHeading level="h2" size="s">
                        {addPaymentHeadingText}
                      </StyledHeading>
                    )}
                    <StripeForm
                      contactInfo={contactInfo}
                      isBillingEqualShipping={!!isBillingEqualShipping}
                      setIsBillingEqualShipping={setIsBillingEqualShipping}
                      saveCardForLater={saveCardForLater}
                      setSaveCardForLater={handleSetSaveCardForLater}
                      handleChangePaymentType={handleChangePaymentType}
                      orderHasSubscription={!isGuest && !!orderHasSubscription}
                      orderHasGasExchangeItems={!!hasGasExchangeItems}
                      isGuest={!checkoutSession?.user}
                    />
                  </StyledNewPaymentForm>
                )}

                {isInvoiceUser && <PoNumberInput />}

                <Button
                  disabled={isContinueButtonDisabled(valid)}
                  htmlType="submit"
                  loading={isLoading}
                >
                  {continueToReviewButtonText}
                </Button>
                <LegalCopy customText={tenant?.brandConfig.customTexts || []} />
              </>
            ) : (
              <StyledSpinner dotSize="16px" />
            )}
          </StyledFieldSet>
        </form>
      )}
    />
  );
};
